import React, { useCallback, useMemo, useState } from 'react';

import { createMongoAbility } from '@casl/ability';
import { AbilityProvider, Can } from '@casl/react';

import { setTenantRoutingContext } from '@/router/tenant-routing';

import { AuthContext } from './_hooks/use-auth';
import { useAuthAnalytics } from './_hooks/use-auth-analytics';
import { useMe } from './_hooks/use-me';
import {
  PermissionOverridesContext,
  usePermissionOverrides,
} from './_hooks/use-permission-overrides';
import { resolveEffectiveAuth } from './ability-from-session';

/**
 * Authentication and Authorization Module
 *
 * This module provides components and hooks for handling user authentication and authorization.
 * The module integrates with CASL for permission management and uses React Router
 * for protected route handling.
 */

export { usePermissionOverrides };

/**
 * Component for handling authorization checks in the UI.
 * Re-exported from `@casl/react` (v7+); must render under {@link AbilityProvider}
 * / {@link AuthProvider}.
 *
 * @example
 * <Can I={Action.READ} a={Resource.APPLICATIONS}>
 *   <RestrictedComponent />
 * </Can>
 */
export { Can };

/**
 * Provider for the AuthContext
 *
 * This provider manages user authentication state and provides:
 * - User session data from the /me endpoint via React Query
 * - Loading states for authentication
 * - Functions to refresh user data
 * - CASL-based permission abilities
 *
 * @param children - The child component to wrap
 * @returns The child component wrapped with the AuthContext
 */
const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: rawUser, isLoading, refetch } = useMe();
  const [overrideEpoch, setOverrideEpoch] = useState(0);

  setTenantRoutingContext(rawUser?.saas_tenants, rawUser?.saas_tenant_id);

  useAuthAnalytics(rawUser, isLoading);

  const notifyPermissionOverridesChanged = useCallback(() => {
    setOverrideEpoch((n) => n + 1);
  }, []);

  const permissionOverridesContextValue = useMemo(
    () => ({
      localOverrideEpoch: overrideEpoch,
      notifyPermissionOverridesChanged,
    }),
    [overrideEpoch, notifyPermissionOverridesChanged]
  );

  const { user, roles, ability } = useMemo(() => {
    if (!rawUser) {
      return { user: null, roles: null, ability: createMongoAbility() };
    }
    return resolveEffectiveAuth(rawUser);
    // overrideEpoch re-reads localStorage override maps inside resolveEffectiveAuth
    // eslint-disable-next-line react-hooks/exhaustive-deps -- overrideEpoch triggers recompute when staff edits overrides
  }, [rawUser, overrideEpoch]);

  const refreshUser = useCallback(async () => {
    try {
      await refetch();
    } catch (error) {
      console.warn('Failed to refresh user session:', error);
    }
  }, [refetch]);

  const contextValue = useMemo(
    () => ({
      loading: isLoading,
      user,
      roles,
      refreshUser,
    }),
    [isLoading, user, roles, refreshUser]
  );

  return (
    <PermissionOverridesContext.Provider
      value={permissionOverridesContextValue}
    >
      <AuthContext.Provider value={contextValue}>
        <AbilityProvider value={ability}>{children}</AbilityProvider>
      </AuthContext.Provider>
    </PermissionOverridesContext.Provider>
  );
};

export { AbilityProvider, AuthProvider };
